import React, { Fragment, FunctionComponent, MouseEvent } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { createMoveable, createUnselectAllAction, MoveablePassableProps } from "@multiweb/picasso";
import { IAppState } from "@multiweb/types/react";
import { getWorkflowEditorInstance, isWorkflowReadOnly } from "../selectors";

type SetComponentProps = {
    readonly isGridEnabled: boolean;
    readonly isWorkflowReadOnly: boolean;
    readonly unselectAll: () => void;
} & MoveablePassableProps;

const SetComponent: FunctionComponent<SetComponentProps> = ({ picassoInstanceKey, isGridEnabled, isWorkflowReadOnly, uiComponent, onLinkChange, renderLink, renderComponent, onResizePointDown, onMouseClick, onMouseUp, onComponentMouseDown: onMouseDown, onMouseLeave, onMouseMove, wrapMapStateToProps, unselectAll }) => {
    const handleClick = (e: MouseEvent) => {
        if (!e.ctrlKey)
            unselectAll();
    };

    const Moveable = React.useMemo(() => createMoveable(wrapMapStateToProps), [wrapMapStateToProps]);

    const gridPattern = isGridEnabled && (
        <Fragment>
            <defs>
                <pattern id="smallGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path d="M 10 0 L 0 0 0 10" fill="none" stroke="gray" strokeWidth="0.5" strokeDasharray="1" />
                </pattern>
                <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
                    <rect width="100" height="100" fill="url(#smallGrid)" />
                    <path d="M 100 0 L 0 0 0 100" fill="none" stroke="gray" strokeWidth="1" />
                </pattern>
            </defs>
            <rect id={uiComponent.index + "rect"} width="100%" height="100%" fill="url(#grid)" />
        </Fragment>
    );

    return (
        <Fragment>
            <svg width="100%" height="100%" style={{ display: "block" }} onClick={handleClick}>
                {gridPattern}
            </svg>
            {uiComponent.children.map(childComponent =>
                <Moveable
                    key={childComponent}
                    index={childComponent}
                    isResizable={false}
                    isDraggable={!isWorkflowReadOnly}
                    {...{
                        picassoInstanceKey,
                        onLinkChange,
                        renderComponent,
                        renderLink,
                        onMouseUp,
                        onResizePointDown,
                        onMouseClick,
                        onMouseLeave,
                        onMouseMove,
                        onMouseDown,
                        wrapMapStateToProps,
                    }} />,
            )}
        </Fragment>
    );
};

type StateProps = Pick<SetComponentProps, "isGridEnabled" | "isWorkflowReadOnly">;

const mapStateToProps: MapStateToProps<StateProps, MoveablePassableProps, IAppState> = (state, { picassoInstanceKey }) => ({
    isGridEnabled: getWorkflowEditorInstance(state, picassoInstanceKey).isGridEnabled,
    isWorkflowReadOnly: isWorkflowReadOnly(state, picassoInstanceKey),
});

type DispatchProps = Pick<SetComponentProps, "unselectAll">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, MoveablePassableProps> = (dispatch, { picassoInstanceKey }) => ({
    unselectAll: () => dispatch(createUnselectAllAction(picassoInstanceKey)),
});

export const Set = connect(mapStateToProps, mapDispatchToProps)(SetComponent);
